import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { outboundUrl, trackOutboundClick } from "../lib/outbound";
import { ExternalLink, Shield, Calendar, Heart, Check, X, Gift, Sparkles, Users, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import RegistryUpdates from "../components/RegistryUpdates";

const STATUS_COLOR = {
  available: "bg-[#E9F6EE] text-[#2D6A4F]",
  claimed: "bg-[#FFF1D6] text-[#8A5A00]",
  purchased: "bg-[#F3E6FA] text-[#6B2186]",
};

function weekOf(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return null;
  return `Week of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function fmt(n) {
  return (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export default function PublicRegistry() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registry, setRegistry] = useState(null);
  const [error, setError] = useState("");
  const [purchaseItem, setPurchaseItem] = useState(null);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [contributeItem, setContributeItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/registry/public/${slug}`);
      setRegistry(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load this registry.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const requireAuth = () => {
    if (!user || typeof user !== "object") {
      toast("Sign in to claim or gift.", {
        action: { label: "Sign in", onClick: () => navigate(`/login?next=/r/${slug}`) },
      });
      return false;
    }
    return true;
  };

  const updateItem = (id, patch) => {
    setRegistry((reg) => reg ? ({
      ...reg,
      items: reg.items.map((i) => i.id === id ? { ...i, ...patch } : i),
    }) : reg);
  };

  const claim = async (item) => {
    if (!requireAuth()) return;
    try {
      await api.post(`/registry/public/${slug}/items/${item.id}/claim`);
      updateItem(item.id, { claimed_status: "claimed", claimed_by: user.id, claimed_by_name: user.display_name, is_mine_to_fulfill: true });
      toast.success("You claimed this item. Thank you.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not claim.");
    }
  };

  const unclaim = async (item) => {
    try {
      await api.post(`/registry/public/${slug}/items/${item.id}/unclaim`);
      updateItem(item.id, { claimed_status: "available", claimed_by: null, claimed_by_name: null, is_mine_to_fulfill: false });
      toast("Released. Someone else can claim it.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not release.");
    }
  };

  const purchase = async (item) => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/registry/public/${slug}/items/${item.id}/purchase`);
      updateItem(item.id, { claimed_status: "purchased", claimed_by: user.id, claimed_by_name: user.display_name, is_mine_to_fulfill: true });
      setPurchaseItem(item);
      setPurchaseResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not proceed.");
    }
  };

  if (loading) {
    return (
      <div className="container-x py-16">
        <p className="font-manrope text-ink-soft animate-soft-pulse" data-testid="public-registry-loading">
          Opening this registry…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-x py-16" data-testid="public-registry-error">
        <div className="card-soft p-10 text-center max-w-xl mx-auto">
          <h1 className="font-outfit text-2xl text-ink">We couldn't open this registry.</h1>
          <p className="font-manrope text-ink-soft mt-3">{error}</p>
          <Link to="/" className="btn-primary mt-6 inline-block">Go home</Link>
        </div>
      </div>
    );
  }

  const available = registry.items.filter((i) => i.claimed_status === "available").length;
  const claimed = registry.items.filter((i) => i.claimed_status === "claimed").length;
  const purchased = registry.items.filter((i) => i.claimed_status === "purchased").length;
  const shareUrl = `${window.location.origin}/r/${registry.slug}`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied.");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  return (
    <div className="container-x py-12 lg:py-16" data-testid="public-registry-page">
      {/* Header */}
      <div className="card-soft p-8 lg:p-12 relative overflow-hidden">
        <div className="max-w-3xl">
          <span className="tag-pill" data-testid="public-header-tag">Hysteregistry</span>
          <h1 className="mt-3 font-outfit text-3xl lg:text-5xl font-medium text-ink tracking-tight" data-testid="public-title">
            {registry.title}
          </h1>
          {registry.description && (
            <p className="mt-4 font-manrope text-ink-soft leading-relaxed">{registry.description}</p>
          )}
          <div className="mt-6 flex flex-wrap gap-4 font-manrope text-sm text-ink-soft">
            <span className="inline-flex items-center gap-2" data-testid="public-owner-name">
              <Heart className="h-4 w-4 text-brand" /> For {registry.owner.display_name}
            </span>
            {registry.owner.expected_surgery_date && (
              <span className="inline-flex items-center gap-2" data-testid="public-surgery-date">
                <Calendar className="h-4 w-4 text-brand" />
                {weekOf(registry.owner.expected_surgery_date)}
              </span>
            )}
            <span className="inline-flex items-center gap-2" data-testid="public-privacy">
              <Shield className="h-4 w-4 text-brand" />
              {registry.owner.has_shipping_address ? "Shipping address on file — private" : "No shipping address yet"}
            </span>
          </div>
          <div className="mt-5 flex gap-2 flex-wrap">
            <span className="tag-pill">{available} available</span>
            <span className="tag-pill !bg-[#FFF1D6] !text-[#8A5A00]">{claimed} claimed</span>
            <span className="tag-pill !bg-[#F3E6FA] !text-[#6B2186]">{purchased} gifted</span>
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            <button onClick={copyShare} className="btn-ghost text-sm" data-testid="public-copy-share">
              Share this registry
            </button>
            {!registry.viewer_authenticated && (
              <Link to={`/login?next=/r/${slug}`} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="public-signin-to-claim">
                <LogIn className="h-4 w-4" /> Sign in to claim a gift
              </Link>
            )}
          </div>
        </div>

        {!registry.owner.has_shipping_address && !registry.is_owner_view && (
          <div className="mt-6 border border-[#F1D6E7] bg-[#FFF5FB] rounded-xl p-4" data-testid="public-no-address-note">
            <p className="font-manrope text-sm text-ink">
              Heads up — {registry.owner.display_name} hasn't saved a shipping address yet. You can still
              claim items; they'll share their address with you another way.
            </p>
          </div>
        )}
      </div>

      {/* Updates feed */}
      <RegistryUpdates readOnly slug={slug} />

      {/* Items */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {registry.items.length === 0 ? (
          <div className="col-span-full card-soft p-10 text-center" data-testid="public-empty-state">
            <p className="font-outfit text-lg text-ink">This registry is still being built.</p>
            <p className="font-manrope text-ink-soft text-sm mt-2">Check back soon.</p>
          </div>
        ) : (
          registry.items.map((item) => {
            const isGroup = !!item.allow_group_gifting;
            const goal = Number(item.price || 0);
            const total = Number(item.total_contributed || 0);
            const pct = goal ? Math.min(100, Math.round((total / goal) * 100)) : 0;
            return (
              <div key={item.id} className="card-soft card-soft-hover p-5 flex flex-col" data-testid={`public-item-${item.id}`}>
                <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-cream-surface">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                  ) : null}
                  <span className={`absolute top-2 left-2 text-xs font-medium px-2 py-1 rounded-full ${STATUS_COLOR[item.claimed_status]}`} data-testid={`public-status-${item.id}`}>
                    {item.claimed_status}
                  </span>
                  {isGroup && (
                    <span className="absolute top-2 right-2 tag-pill !bg-white/90 backdrop-blur inline-flex items-center gap-1 text-xs" data-testid={`public-group-badge-${item.id}`}>
                      <Users className="h-3 w-3" /> Group gift
                    </span>
                  )}
                </div>
                <div className="mt-4 flex-1">
                  <span className="tag-pill text-xs">{item.category}</span>
                  <h3 className="mt-2 font-outfit text-base font-medium text-ink line-clamp-2" data-testid={`public-name-${item.id}`}>
                    {item.product_name}
                  </h3>
                  {item.price != null && (
                    <p className="mt-1 font-manrope text-sm text-ink-soft">${item.price.toFixed(2)}</p>
                  )}
                  {isGroup && goal > 0 && (
                    <div className="mt-3" data-testid={`public-progress-${item.id}`}>
                      <div className="flex justify-between font-manrope text-xs text-ink-soft mb-1">
                        <span>{fmt(total)} of {fmt(goal)}</span>
                        <span>{pct}%</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                      {(item.contributions || []).length > 0 && (
                        <p className="mt-2 font-manrope text-xs text-ink-soft">
                          {item.contributions.length} {item.contributions.length === 1 ? "person has" : "people have"} chipped in
                        </p>
                      )}
                    </div>
                  )}
                  {item.claimed_by_name && !isGroup && (
                    <p className="mt-2 font-manrope text-xs text-ink-soft" data-testid={`public-claimed-by-${item.id}`}>
                      {item.claimed_status === "purchased" ? "Gifted by" : "Claimed by"} {item.claimed_by_name}
                    </p>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                  <a
                    href={outboundUrl(item)}
                    target="_blank"
                    rel="noreferrer sponsored"
                    onClick={() => trackOutboundClick({ kind: "item", itemId: item.id, registrySlug: slug, destination: outboundUrl(item) })}
                    className="text-sm text-brand font-medium inline-flex items-center gap-1 hover:underline"
                    data-testid={`public-view-${item.id}`}
                  >
                    View <ExternalLink className="h-3.5 w-3.5" />
                  </a>

                  {isGroup && item.claimed_status !== "purchased" && (
                    <button onClick={() => requireAuth() && setContributeItem(item)} className="btn-primary text-sm inline-flex items-center gap-1" data-testid={`public-contribute-${item.id}`}>
                      <Sparkles className="h-4 w-4" /> Contribute
                    </button>
                  )}

                  {!isGroup && item.claimed_status === "available" && (
                    <div className="flex gap-2">
                      <button onClick={() => claim(item)} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid={`public-claim-${item.id}`}>
                        <Check className="h-4 w-4" /> I'll get this
                      </button>
                      <button onClick={() => purchase(item)} className="btn-primary text-sm inline-flex items-center gap-1" data-testid={`public-purchase-${item.id}`}>
                        <Gift className="h-4 w-4" /> Purchase
                      </button>
                    </div>
                  )}

                  {!isGroup && item.claimed_status === "claimed" && item.is_mine_to_fulfill && (
                    <div className="flex gap-2">
                      <button onClick={() => unclaim(item)} className="btn-ghost text-sm inline-flex items-center gap-1" data-testid={`public-unclaim-${item.id}`}>
                        <X className="h-4 w-4" /> Release
                      </button>
                      <button onClick={() => purchase(item)} className="btn-primary text-sm inline-flex items-center gap-1" data-testid={`public-purchase-claimed-${item.id}`}>
                        <Gift className="h-4 w-4" /> Purchase
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Purchase confirmation modal */}
      <Dialog open={!!purchaseItem} onOpenChange={(open) => { if (!open) { setPurchaseItem(null); setPurchaseResult(null); } }}>
        <DialogContent data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">Thank you. Truly.</DialogTitle>
            <DialogDescription>
              We've marked this as purchased so no one gifts it twice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-brand-soft rounded-xl p-4 font-manrope text-sm text-ink" data-testid="purchase-address-notice">
              <Shield className="inline h-4 w-4 mr-2 text-brand" />
              {purchaseResult?.owner_has_shipping_address
                ? <>Ship directly to <strong>{purchaseResult?.owner_display_name || "the registry owner"}</strong> using the shipping address on file. Their address stays private — you won't see it here.</>
                : <>{purchaseResult?.owner_display_name || "The registry owner"} hasn't saved a shipping address yet. Reach out to them to confirm where to send it.</>
              }
            </div>
            <p className="font-manrope text-sm text-ink-soft">
              Clicking below opens the retailer in a new tab — use your own checkout there. You can
              always come back and mark it unpurchased if plans change.
            </p>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => { setPurchaseItem(null); setPurchaseResult(null); }} data-testid="purchase-close">Close</button>
            {purchaseResult?.redirect_url && (
              <a href={purchaseResult.redirect_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="purchase-go-btn">
                Continue to retailer <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group gift contribute dialog */}
      <ContributeDialog
        slug={slug}
        item={contributeItem}
        user={user}
        onClose={() => setContributeItem(null)}
        onContributed={(data) => {
          // Refresh registry to get updated contributions
          load();
          toast.success(data.fully_funded ? "You finished funding this gift! 💖" : `Thank you — $${data.amount_contributed.toFixed(2)} added.`);
        }}
      />
    </div>
  );
}

function ContributeDialog({ slug, item, user, onClose, onContributed }) {
  const open = !!item;
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (item) {
      const goal = Number(item.price || 0);
      const total = Number(item.total_contributed || 0);
      const remaining = Math.max(0, goal - total);
      setAmount(remaining > 25 ? "25" : remaining.toFixed(2));
      setResult(null);
    }
  }, [item]);

  if (!item) return null;
  const goal = Number(item.price || 0);
  const total = Number(item.total_contributed || 0);
  const remaining = Math.max(0, goal - total);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/registry/public/${slug}/items/${item.id}/contribute`, {
        amount: parseFloat(amount),
      });
      setResult(data);
      onContributed(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not contribute.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="contribute-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">Chip in on "{item.product_name}"</DialogTitle>
          <DialogDescription>
            Multiple people can contribute. When the goal is met, we'll mark it as fully funded.
          </DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="contribute-success">
            <div className="bg-brand-soft rounded-xl p-4 font-manrope text-sm text-ink">
              {result.fully_funded
                ? <>🎉 You finished it off! Total raised: <strong>{fmt(result.total)}</strong>. The item is now marked purchased so the owner knows it's on the way.</>
                : <>Thanks for contributing <strong>{fmt(result.amount_contributed)}</strong>. Running total: <strong>{fmt(result.total)}</strong> / {fmt(result.goal)}.</>
              }
            </div>
            <p className="font-manrope text-sm text-ink-soft">
              This MVP tracks contributions only. Once fully funded, please coordinate actually buying
              and shipping the item with the other contributors — use the registry's share link to see
              who pitched in.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn-primary" data-testid="contribute-done">Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="bg-cream-surface rounded-xl p-4 font-manrope text-sm text-ink">
              Goal: <strong>{fmt(goal)}</strong> · Raised so far: <strong>{fmt(total)}</strong> · Remaining: <strong>{fmt(remaining)}</strong>
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Your contribution (USD)</label>
              <input
                type="number"
                step="0.01"
                min="0.5"
                max={remaining}
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input-soft"
                data-testid="contribute-amount-input"
              />
              <p className="font-manrope text-xs text-ink-soft mt-1">
                Contributing as <strong>{user?.display_name}</strong>.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn-ghost" data-testid="contribute-cancel">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="contribute-submit">
                {submitting ? "Contributing…" : "Contribute"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
